import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { StiAuthService } from './sti-auth.service';
import { StiBook, StiBookFileResponse, StiUserResponse } from './sti.models';
import { createStiBookFile, decryptStiBookBuffer } from './sti-book-decryptor';

@Injectable({
    providedIn: 'root'
})
export class StiApiService {
    private readonly userUrl = environment.sti.baseUrl + 'auth/user/';
    private readonly booksUrl = environment.sti.baseUrl + 'api/v1/books/';
    private readonly bookFileByIdUrl = environment.sti.baseUrl + 'api/v1/books/id/';

    private readonly http = inject(HttpClient);
    private readonly stiAuthService = inject(StiAuthService);

    public async getBooksForCurrentUser(): Promise<StiBook[]> {
        const authorization = this.getAuthorizationHeaderOrThrow();
        const userId = await this.getUserId(authorization);
        return this.getBooksForUserById(userId, authorization);
    }

    public async getBookFile(bookFileId: string): Promise<File> {
        const authorization = this.getAuthorizationHeaderOrThrow();
        const fileInfo = await this.getBookFileById(bookFileId, authorization);
        return this.downloadAndDecryptBookFile(fileInfo);
    }

    private async downloadAndDecryptBookFile(fileInfo: StiBookFileResponse): Promise<File> {
        const encryptedBuffer = await this.downloadEncryptedBookFile(fileInfo.url);
        const decryptedBuffer = await decryptStiBookBuffer(encryptedBuffer, fileInfo);
        return createStiBookFile(decryptedBuffer, fileInfo.name);
    }

    private async getUserId(authorization: string): Promise<string> {
        const response = await firstValueFrom(this.http.get<StiUserResponse>(this.userUrl, {
            headers: {
                Authorization: authorization,
            }
        }));

        if (!response?.success || !response?.user_id) {
            throw new Error('Failed to retrieve STI user id.');
        }

        return response.user_id;
    }

    private async getBooksForUserById(userId: string, authorization: string): Promise<StiBook[]> {
        return firstValueFrom(this.http.post<StiBook[]>(this.booksUrl, {
            user: {
                id: userId,
            },
        }, {
            headers: {
                Authorization: authorization,
            }
        }));
    }

    private async getBookFileById(bookFileId: string, authorization: string): Promise<StiBookFileResponse> {
        return firstValueFrom(this.http.post<StiBookFileResponse>(this.bookFileByIdUrl, {
            book: {
                id: bookFileId,
            },
        }, {
            headers: {
                Authorization: authorization,
            }
        }));
    }

    private async downloadEncryptedBookFile(url: string): Promise<ArrayBuffer> {
        return firstValueFrom(this.http.get(url, { responseType: 'arraybuffer' }));
    }

    private getAuthorizationHeaderOrThrow(): string {
        return this.stiAuthService.getAuthorizationHeaderValue();
    }
}
